/**
 * Single source of truth for Saudi phone normalization on the frontend.
 *
 * Canonical DB format: `966XXXXXXXXX` (12 digits, no `+`). Matches what
 * GoTrue v2.185+ stores in `auth.users.phone` after stripping the `+`.
 * Display format: `+966 5X XXX XXXX`. GoTrue admin API + Unifonic still
 * receive `+966...` because they require E.164 with `+`.
 */

/**
 * `allowLandline` widens the accepted national number from a mobile
 * (`5XXXXXXXX`) to any 9-digit Saudi number (`1XXXXXXXX` landlines, `9200…`
 * unified numbers) — for company/organisation phones only; personal and login
 * phones stay mobile-only.
 */
export interface SaudiPhoneOptions {
  allowLandline?: boolean;
}

/**
 * Normalize any user-entered Saudi phone (with or without country code,
 * with or without `+`, with spaces/dashes) into the canonical DB format.
 * Returns `null` if the input isn't a valid Saudi mobile (or, with
 * `allowLandline`, any valid 9-digit Saudi number).
 */
export function normalizePhoneForDB(
  input: string | null | undefined,
  { allowLandline = false }: SaudiPhoneOptions = {},
): string | null {
  if (!input) return null;
  const national = allowLandline ? '[1-9]\\d{8}' : '5\\d{8}';
  let s = String(input).replace(/[\s\-()]/g, '');
  s = s.replace(/^\+/, '').replace(/^00/, '');
  if (new RegExp(`^0?${national}$`).test(s)) {
    s = '966' + s.replace(/^0/, '');
  }
  return new RegExp(`^966${national}$`).test(s) ? s : null;
}

export function isValidSaudiPhone(
  input: string | null | undefined,
  options?: SaudiPhoneOptions,
): boolean {
  return normalizePhoneForDB(input, options) !== null;
}

/**
 * GoTrue's admin API (`auth.admin.createUser`) and Unifonic require E.164
 * with `+`. Use this when calling those — never for DB writes.
 */
export function toGoTruePhone(canonical: string): string {
  return canonical.startsWith('+') ? canonical : `+${canonical}`;
}

/**
 * Pretty-print a stored canonical phone for display: `+966 5X XXX XXXX`.
 * Falls back to the raw value if it's not in canonical form.
 */
export function displayPhone(stored: string | null | undefined): string {
  const n = normalizePhoneForDB(stored);
  if (!n) return stored ?? '';
  return `+966 ${n.slice(3, 5)} ${n.slice(5, 8)} ${n.slice(8)}`;
}

/**
 * Any stored Saudi phone shape (`966…`, `+966…`, `05…`, `5…`) → E.164
 * (`+9665XXXXXXXX`), the value `<PhoneInput>` takes and emits — use it to
 * populate edit forms. Returns empty string if the input isn't a valid Saudi
 * mobile.
 */
export function toE164Phone(
  stored: string | null | undefined,
  options?: SaudiPhoneOptions,
): string {
  const n = normalizePhoneForDB(stored, options);
  return n ? `+${n}` : '';
}

/**
 * Given a canonical `966XXXXXXXXX`, return every plausible historical storage
 * form (E.164 with `+`, local `0`-prefix, bare 9-digit suffix) so a same-suffix
 * lookup hits rows written in mixed legacy shapes. Order is irrelevant
 * (PostgREST `IN(...)` is unordered). Returns just the canonical form if the
 * input isn't canonical.
 */
export function buildPhoneCandidates(canonical: string | null | undefined): string[] {
  if (!canonical) return [];
  const out = new Set<string>([canonical, `+${canonical}`]);
  // canonical = "966" + 9-digit national number, by construction.
  const national = canonical.startsWith('966') ? canonical.slice(3) : null;
  if (national && national.length === 9) {
    out.add(`0${national}`);
    out.add(national);
  }
  return Array.from(out);
}
